type NavigationActivity = {
  readonly token: symbol;
  readonly source: URL;
  readonly destination: URL;
};
const interactions = new Set<symbol>();
const listeners = new Set<() => void>();
let committedAddress: string | null = null;
let pending: NavigationActivity | null = null;

function notify() {
  for (const listener of listeners) listener();
}

export function subscribeNavigationActivity(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function automaticNavigationAllowed(): boolean {
  return interactions.size === 0 && pending === null;
}

export function acquireNavigationInteraction(): () => void {
  const token = Symbol();
  interactions.add(token);
  notify();
  return () => {
    if (interactions.delete(token)) notify();
  };
}

export function beginNavigationActivity({
  href,
  navigationType,
}: {
  readonly href: string;
  readonly navigationType?: ("push" | "replace" | "traverse") | undefined;
}) {
  const browserSource = new URL(window.location.href);
  const source =
    navigationType === "traverse"
      ? new URL(committedAddress ?? browserSource.href, browserSource)
      : browserSource;
  const destination = new URL(href, browserSource);

  if (destination.origin !== browserSource.origin) return;

  if (
    destination.pathname === source.pathname &&
    destination.search === source.search
  ) {
    completeNavigationActivity({});
    return;
  }

  const token = Symbol();
  pending = { token, source, destination };
  notify();
  return token;
}

export function completeNavigationActivity({
  token,
}: {
  readonly token?: symbol | undefined;
}) {
  if (token !== undefined && pending?.token !== token) return;

  pending = null;
  notify();
}

export function commitNavigationActivity({
  pathname,
  search,
}: {
  readonly pathname: string;
  readonly search: string;
}): void {
  committedAddress = pathname + search;

  if (!pending) return;

  const destinationReached =
    pathname === pending.destination.pathname &&
    search === pending.destination.search;
  const sourceLeft = pathname !== pending.source.pathname;

  if (destinationReached || sourceLeft)
    completeNavigationActivity({ token: pending.token });
}
